import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDate, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  formatLumPeriodRange,
  getDateFromMinutes,
  getLumPeriodCalendarStyles,
} from 'src/app/services/LUM/lum.helper';


const FULL_DAY_START_MIN = 0;
const FULL_DAY_END_MIN = 1439;

@Component({
  selector: 'app-modal-lum-period',
  imports: [CommonModule, FormsModule, TranslateModule, NgbDatepickerModule],
  templateUrl: './modal-lum-period.component.html',
  styleUrl: './modal-lum-period.component.scss',
})
export class ModalLumPeriodComponent implements OnInit {


  @Input() period: any | null = null;
  year = new Date().getFullYear();
  editStage: 'start' | 'end' = 'start';
  editingId: number | null = null;
  draft: any;

  constructor(public activeModal: NgbActiveModal) {
    this.draft = this.createEmptyDraft();
  }

  ngOnInit(): void {
    if (this.period != null) {
      this.applyPeriodToDraft(this.period);
    }
  }

  createEmptyDraft(): any {
    return {
      name: '',
      type: 1,
      enabled: true,
      scheduleMode: 'fullDays',
      start_day: 1,
      start_month: 1,
      start_min: 0,
      startTimeStr: '00:00',
      end_day: 1,
      end_month: 1,
      end_min: 1439,
      endTimeStr: '23:59',
    };
  }

  applyPeriodToDraft(period: any): void {
    this.draft.id = period.id;
    this.draft.name = String(period?.name ?? '').trim();
    this.draft.type = period?.type;
    this.draft.enabled = period?.enabled;
    this.draft.scheduleMode = this.isFullDayPeriod() ? 'fullDays' : 'timed';
    this.draft.start_day = Number(period?.start_day) || 1;
    this.draft.start_month = Number(period?.start_month) || 1;
    this.draft.start_min = period?.start_min || 0;
    this.draft.startTimeStr = getDateFromMinutes(period?.start_min);
    this.draft.end_day = Number(period?.end_day) || 1;
    this.draft.end_month = Number(period?.end_month) || 1;
    this.draft.end_min = period?.end_min || 0;
    this.draft.endTimeStr = getDateFromMinutes(period?.end_min);
    this.editingId = period?.id ?? null;
    this.editStage = 'start';
  }


  isFullDayPeriod(): boolean {
    if (this.period?.start_min === FULL_DAY_START_MIN && this.period?.end_min === FULL_DAY_END_MIN) {
      return true;
    }
    return false;
  }

  getrange(): string {
    return formatLumPeriodRange(this.draft);
  }

  canSave(): boolean {
    return String(this.draft?.name ?? '').trim().length > 0;
  }

  periodColorStyles(): Record<string, string> {
    return getLumPeriodCalendarStyles(Number(this.draft?.id));
  }


  onCalendarSelect(date: NgbDate): void {
    if (this.editStage === 'start') {
      this.draft.start_day = date.day;
      this.draft.start_month = date.month;
      this.editStage = 'end';
      return;
    }

    this.draft.end_day = date.day;
    this.draft.end_month = date.month;
    this.editStage = 'start';
  }

  getDayClasses(date: NgbDate): Record<string, boolean> {
    const start = new NgbDate(this.year, Number(this.draft.start_month) || 1, Number(this.draft.start_day) || 1);
    const end = new NgbDate(this.year, Number(this.draft.end_month) || 1, Number(this.draft.end_day) || 1);
    const wraps = end.before(start);
    const isStart = date.equals(start);
    const isEnd = date.equals(end);
    const inside = !isStart && !isEnd && (wraps
      ? date.after(start) || date.before(end)
      : date.after(start) && date.before(end));

    if (!isStart && !isEnd && !inside) {
      return { 'custom-day': true };
    }

    return {
      'custom-day': true,
      'range': true,
      'inside': inside,
      'edge': isStart || isEnd,
      'start': isStart,
      'end': isEnd,
    };
  }

  onScheduleModeChange(mode: 'fullDays' | 'timed'): void {
    if (mode === 'timed' && this.draft.startTimeStr === '00:00' && this.draft.endTimeStr === '23:59') {
      this.draft.startTimeStr = '00:00';
      this.draft.endTimeStr = '23:59';
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const payload: Record<string, unknown> = {
      ...this.draft,
      name: String(this.draft.name ?? '').trim(),
    };
    const editId = Number(this.editingId);
    if (Number.isFinite(editId) && editId >= 1) {
      payload['id'] = editId;
    } else {
      delete payload['id'];
    }
    this.activeModal.close(payload);
  }
}
